import React, { useState, useEffect } from 'react';
import { Minus, Plus, Trash2, Heart, Info,AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const CartItem = ({ item, onQuantityChange, onRemove, onMoveToWishlist, onCustomizeClick }) => {
  const [quantity, setQuantity] = useState(item.quantity || 1);

  const navigate = useNavigate();

  // Get minimum order quantity
  const minimumOrderQty = item?.minimumOrderQty || item?.min_order_qty || 1;

  // Ensure quantity is never below minimum order quantity
  useEffect(() => {
    if (quantity < minimumOrderQty) {
      setQuantity(minimumOrderQty);
      onQuantityChange(item.id, minimumOrderQty);
    }
  }, [minimumOrderQty, quantity, item.id, onQuantityChange]);

  const handleQuantityChange = (newQuantity) => {
    // Ensure quantity doesn't go below minimum order quantity
    const validQuantity = Math.max(newQuantity, minimumOrderQty);

    if (validQuantity !== newQuantity && newQuantity < minimumOrderQty) {
      toast.error(`Minimum order quantity is ${minimumOrderQty} units`, {
        duration: 3000,
        icon: <AlertTriangle size={16} className="text-orange-600" />,
      });
    }

    setQuantity(validQuantity);
    onQuantityChange(item.id, validQuantity);
  };

  const handleInputChange = (e) => {
    const inputValue = parseInt(e.target.value) || minimumOrderQty;
    handleQuantityChange(inputValue);
  };

  const handleCustomizeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCustomizeClick) {
      onCustomizeClick(item);
    }
  };

  // Get prices from API response - handle multiple field formats
  const getPriceInfo = (item) => {
    // New API structure: price = max_retail_price (original), offerPrice = your_price (sale price)
    let originalPrice = null;
    let offerPrice = null;

    // Try new structure first
    if (item.price !== undefined) {
      originalPrice = Number(item.price) || 0;
    }

    if (item.offerPrice !== undefined) {
      offerPrice = Number(item.offerPrice) || 0;
    }

    // Fallback to old structure if new structure not available
    if (!originalPrice && item.max_retail_price) {
      originalPrice = Number(item.max_retail_price) || 0;
    }

    if (!offerPrice && item.your_price) {
      offerPrice = Number(item.your_price) || 0;
    }

    // Final fallback to legacy price field
    if (!originalPrice && !offerPrice && item.price) {
      const legacyPrice = typeof item.price === "string"
        ? parseFloat(item.price.replace(/[₹$]/g, ''))
        : Number(item.price);
      originalPrice = legacyPrice || 0;
    }

    return { originalPrice, offerPrice };
  };

  const { originalPrice, offerPrice } = getPriceInfo(item);

  // Determine current price and if on sale
  let currentPrice;
  let displayOriginalPrice = null;
  let isOnSale = false;

  // If we have both prices and offer price is less than original price
  if (originalPrice > 0 && offerPrice > 0 && offerPrice < originalPrice) {
    currentPrice = offerPrice;
    displayOriginalPrice = originalPrice;
    isOnSale = true;
  }
  // If we only have offer price
  else if (offerPrice > 0) {
    currentPrice = offerPrice;
  }
  // If we only have original price
  else if (originalPrice > 0) {
    currentPrice = originalPrice;
  }
  // Final fallback
  else {
    currentPrice = 0;
  }

  // Calculate subtotal
  const subtotal = (currentPrice * quantity).toFixed(2);

  return (
    <article className="flex flex-col sm:flex-row gap-4 p-4 bg-white bg-opacity-60 rounded-xl border border-orange-200 shadow-sm hover:shadow-md transition-shadow">
      {/* Product Image */}
      <div className="relative flex-shrink-0 cursor-pointer" onClick={() => navigate(`/product/${item.id}`)}>
        <img
          src={item.image}
          alt={`${item.name} product image`}
          className="w-full sm:w-24 md:w-32 h-32 sm:h-24 md:h-32 object-cover rounded-lg transition-opacity"
          loading="lazy"
        />

        {/* Customizable Badge */}
        {item.isCustomizable && (
          <button
            onClick={handleCustomizeClick}
            className="absolute top-1 left-1 bg-gradient-to-r from-orange-400 to-yellow-400 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-300"
            aria-label={`Customize ${item.name}`}
          >
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              Edit
            </span>
          </button>
        )}
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-lg mb-1 line-clamp-2 cursor-pointer transition-colors text-gray-800" onClick={() => navigate(`/product/${item.id}`)}>
              {item.name}
            </h3>
            <p className="text-sm text-gray-600 mb-2">{item.categoryName || item.categoryId || 'Uncategorized'}</p>
            {/* {minimumOrderQty > 1 && (
              <div className="flex items-center gap-1 text-xs text-blue-600 mb-2">
                <Info className="w-3 h-3" />
                <span>Min. order: {minimumOrderQty} units</span>
              </div>
            )} */}
          </div>

          {/* Enhanced Price Section */}
          <div className="text-right">
            <div className="flex flex-col items-start sm:items-end">
              <div className="flex flex-row gap-2 items-end">
                <span className={`text-lg font-semibold ${
                isOnSale ? 'text-red-500' : 'text-gray-800'
              }`}>
                ₹{currentPrice.toFixed(2)}
              </span>
              {isOnSale && displayOriginalPrice && (
                <span className="text-sm text-gray-500 line-through mb-1">
                  ₹{displayOriginalPrice.toFixed(2)}
                </span>
              )}
              
              </div>
              <span className="text-sm text-gray-600">
                each
              </span>
              {/* {isOnSale && displayOriginalPrice && (
                <span className="text-xs text-green-600 font-medium">
                  Save ₹{(displayOriginalPrice - currentPrice).toFixed(2)}
                </span>
              )} */}
            </div>
          </div>
        </div>

        {/* Quantity and Actions */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          {/* Quantity Controls */}
          <div className="flex items-center gap-3">
            <label htmlFor={`quantity-${item.id}`} className="text-sm font-medium text-gray-700">
              Qty:
            </label>
            <div className="flex items-center border rounded-lg border-gray-300">
              <button
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= minimumOrderQty}
                className="p-3 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-l-lg"
                aria-label="Decrease quantity"
                title={quantity <= minimumOrderQty ? `Minimum order quantity is ${minimumOrderQty}` : 'Decrease quantity'}
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                id={`quantity-${item.id}`}
                type="number"
                min={minimumOrderQty}
                value={quantity}
                onChange={handleInputChange}
                style={{
                  MozAppearance: "textfield",
                }}
                onWheel={(e) => e.target.blur()}
                className="w-16 px-2 py-2 text-center border-0 focus:outline-none focus:ring-0 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label={`Quantity for ${item.name}`}
                title={`Minimum quantity: ${minimumOrderQty}`}
              />
              <button
                onClick={() => handleQuantityChange(quantity + 1)}
                className="p-3 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-r-lg"
                aria-label="Increase quantity"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Subtotal Display */}
            <div className="text-lg font-semibold text-teal-700">
              ₹{subtotal}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onRemove(item.id)}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
              aria-label={`Remove ${item.name} from cart`}
            >
              <Trash2 className="w-4 h-4" />
              <span className="inline sm:inline">Remove</span>
            </button>
          </div>
        </div>

        {/* Additional Info for Minimum Order Quantity */}
        {minimumOrderQty > 1 && quantity === minimumOrderQty && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> This product has a minimum order quantity of {minimumOrderQty} units.
            </p>
          </div>
        )}
      </div>
    </article>
  );
};

export default CartItem;
